'use client';

import { faEraser } from '@fortawesome/pro-light-svg-icons';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withEfTid } from '@pickleballinc/lib/everflow/url';
import { Button, ButtonVariant, Checkbox, Radio } from '@pickleballinc/react-ui';
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from '@pickleballinc/ui/components/ui/credenza';
import { Popover, PopoverContent, PopoverTrigger } from '@pickleballinc/ui/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@pickleballinc/ui/components/ui/toggle-group';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useContext, useRef, useState } from 'react';

import { Select } from '@/components/ReactUISelect/ReactUISelect';
import { ParamlessWebviewScreenId } from '@/modules/players/v3/constants/webview-screen-ids';
import { CardContext } from '@/modules/players/v3/contexts/card-context';
import { useIsMobileCard } from '@/modules/players/v3/hooks/use-is-mobile-card';
import { useWebviewNavigation } from '@/modules/players/v3/hooks/use-webview-navigation';
import { isSelectOption } from '@/modules/players/v3/utils/utils';
import { EVERFLOW_DOMAINS } from '@/utils/helpers/constants';

type CardRootProps = React.ComponentProps<'div'> & {
  footer?: React.ReactElement;
};
const CardRootUI = ({ children, footer, className, ...props }: CardRootProps) => {
  const isMobile = useIsMobileCard();

  return (
    <CardContext.Provider value={{ isMobile, isWidget: false }}>
      <div
        className={clsx(
          'relative flex flex-col items-center justify-center rounded-lg bg-card shadow-sm w-full h-auto',
          className
        )}
        {...props}
      >
        {children}
        {footer ?? null}
      </div>
    </CardContext.Provider>
  );
};

type CardTitleProps = React.ComponentProps<'h3'>;
const CardTitleUI = ({ children, className, ...props }: CardTitleProps) => {
  return (
    <h3 className={clsx('font-semibold text-base text-gray-900 truncate', className)} {...props}>
      {children}
    </h3>
  );
};

type CardHeaderProps = React.ComponentProps<'div'>;
const CardHeaderUI = ({ children, className, ...props }: CardHeaderProps) => {
  return (
    <div
      className={clsx(
        'flex w-full items-center justify-between border-b border-gray-200 px-6 py-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

type CardContentProps = React.ComponentProps<'div'>;
const CardContentUI = ({ children, className, ...props }: CardContentProps) => {
  return (
    <div
      className={clsx(
        'relative flex flex-col justify-start gap-4 px-4 py-3 w-full h-auto',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

interface CardLinkProps extends Omit<React.ComponentProps<typeof Button>, 'children'> {
  href: string;
  isOutsideLink?: boolean;
  children?: React.ReactNode;
  variant?: ButtonVariant;
  disabled?: boolean;
  screenId?: ParamlessWebviewScreenId;
}
const CardLinkUI = ({
  href,
  className,
  children = 'Show all',
  variant = 'link',
  disabled,
  isOutsideLink,
  screenId,
  onClick,
  ...props
}: CardLinkProps) => {
  const router = useRouter();
  const { navigateToNativeScreenIfSupported } = useWebviewNavigation();

  return (
    <Button
      {...props}
      variant={variant}
      size='sm'
      className={clsx('!text-gray-900 hover:!text-gray-900 hover:underline', className)}
      disabled={disabled}
      onClick={(event) => {
        onClick?.(event);
        if (screenId && navigateToNativeScreenIfSupported(screenId)) {
          return;
        }
        if (isOutsideLink) {
          window.open(withEfTid(href, EVERFLOW_DOMAINS), '_blank');
        } else {
          router.push(href);
        }
      }}
    >
      {children}
    </Button>
  );
};

type CardImgProps = React.ComponentProps<'img'>;

const CardImgUI = ({ className, ...props }: CardImgProps) => {
  return (
    <img
      {...props}
      width={109}
      height={21}
      className={clsx('w-28 h-6', className)}
      alt={props.alt}
    />
  );
};

export enum CardFilterType {
  MULTIPLE = 'multiple',
  SINGLE = 'single',
}

export interface Filter {
  key: string;
  type: CardFilterType;
  style?: 'toggle' | 'select' | 'radio';
  title: string;
  items: {
    label: string;
    value: string;
  }[];
}

interface CardFilterProps {
  filters: Filter[];
  className?: string;
  onApply?: (filters: string[], saveForNextTime: boolean) => void;
  hasActiveFilters?: boolean;
  allowSaveFilters?: boolean;
  isSaveFiltersForNextTimeActive?: boolean;
  initialSelectedFilters?: Record<string, string[]>;
  isWebview?: boolean;
  immediate?: boolean;
  label?: string;
  heading?: { title: string; description?: string };
  headerSlot?: React.ReactNode;
}

const DEFAULT_FILTER_HEADING = { title: 'Filters', description: 'Apply filters to your search' };

const CardFilterUI = ({
  filters,
  className,
  onApply,
  hasActiveFilters,
  allowSaveFilters,
  isSaveFiltersForNextTimeActive,
  initialSelectedFilters,
  isWebview,
  immediate = false,
  label,
  heading = DEFAULT_FILTER_HEADING,
  headerSlot,
}: CardFilterProps) => {
  const { isWidget } = useContext(CardContext);
  const isMobile = useIsMobileCard();
  const [open, setOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<Record<string, string[]>>(
    initialSelectedFilters || {}
  );
  const [saveForNextTime, setSaveForNextTime] = useState(isSaveFiltersForNextTimeActive || false);
  const snapshotRef = useRef<Record<string, string[]>>({});
  const appliedRef = useRef(false);

  // Immediate consumers own their selection upstream, so it survives a pills<->popover swap;
  // batch consumers edit a local draft the Apply button commits.
  const selectedFilters = immediate ? initialSelectedFilters || {} : draftFilters;

  const updateFilters = (updater: (prev: Record<string, string[]>) => Record<string, string[]>) => {
    const next = updater(selectedFilters);
    if (immediate) {
      onApply?.(Object.values(next).flat(), false);
    } else {
      setDraftFilters(next);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!immediate) {
      if (newOpen) {
        snapshotRef.current = { ...selectedFilters };
        appliedRef.current = false;
      } else if (!appliedRef.current) {
        setDraftFilters(snapshotRef.current);
      }
    }
    setOpen(newOpen);
  };

  const handleApply = () => {
    appliedRef.current = true;
    setOpen(false);
    onApply?.(Object.values(selectedFilters).flat(), saveForNextTime);
  };

  const shouldRenderPills = immediate && !isWidget && !isMobile;

  const pillClassName = clsx(
    'shadow-none z-10 px-3 py-2 h-auto min-h-9 rounded-md text-gray-700 text-xs whitespace-nowrap outline-0 data-[state=on]:z-20 data-[state=on]:bg-brand-700 data-[state=on]:text-white',
    shouldRenderPills
      ? 'border border-gray-300 bg-white data-[state=on]:border-brand-700'
      : 'border-none bg-gray-50 data-[state=on]:bg-transparent data-[state=on]:border-none'
  );

  const filterGroups = filters.map(({ type, items, title, key, style = 'toggle' }) => {
    const groupSelectedFilters = selectedFilters[key] || [];
    const toggleItems = items.map((filter) => (
      <ToggleGroupItem key={filter.value} value={filter.value} className={pillClassName}>
        <span className='z-10'>{filter.label}</span>
      </ToggleGroupItem>
    ));

    return (
      <div key={key} className='flex flex-col gap-y-4'>
        {title && !shouldRenderPills ? (
          <p className='text-gray-700 font-semibold'>{title}</p>
        ) : null}
        {style === 'toggle' ? (
          type === CardFilterType.MULTIPLE ? (
            <ToggleGroup
              variant='outline'
              size='sm'
              type='multiple'
              value={groupSelectedFilters}
              className='items-start justify-start flex flex-wrap gap-x-1 gap-y-2'
              onValueChange={(newValue: string[]) => {
                updateFilters((prev) => ({ ...prev, [key]: newValue }));
              }}
            >
              {toggleItems}
            </ToggleGroup>
          ) : (
            <ToggleGroup
              variant='outline'
              size='sm'
              type='single'
              value={groupSelectedFilters[0]}
              className='items-start justify-start flex flex-wrap gap-x-1 gap-y-2'
              onValueChange={(newValue: string) => {
                updateFilters((prev) => ({ ...prev, [key]: newValue ? [newValue] : [] }));
              }}
            >
              {toggleItems}
            </ToggleGroup>
          )
        ) : style === 'select' ? (
          <Select
            options={items}
            value={groupSelectedFilters[0]}
            onChange={(newValue: unknown) => {
              if (!isSelectOption(newValue)) return;
              updateFilters((prev) => ({
                ...prev,
                [key]: newValue.value ? [newValue.value] : [],
              }));
            }}
          />
        ) : style === 'radio' ? (
          <div className='flex items-center gap-8'>
            {items.map((option) => (
              <Radio
                key={option.value}
                name={option.value}
                id={option.value}
                Text={option.label}
                value={option.value}
                defaultChecked={groupSelectedFilters[0] === option.value}
                onChange={(e) => {
                  if (e.currentTarget.value === groupSelectedFilters[0]) {
                    updateFilters((prev) => ({ ...prev, [key]: [] }));
                  } else {
                    updateFilters((prev) => ({
                      ...prev,
                      [key]: [e.currentTarget.value],
                    }));
                  }
                }}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  });

  const filterActions = (
    <div className='flex items-center justify-between gap-2'>
      <div className='flex items-center gap-4'>
        <Button
          variant='link-gray'
          size='sm'
          className='flex items-center gap-2'
          onClick={() => setDraftFilters({})}
        >
          <FontAwesomeIcon icon={faEraser} size='lg' />
          Clear all
        </Button>
        {allowSaveFilters ? (
          <div className='flex items-center'>
            <Checkbox
              Text='Save filters for next time'
              size='sm'
              onChange={(e) => setSaveForNextTime(e.target.checked)}
              checked={saveForNextTime}
            />
          </div>
        ) : null}
      </div>
      <div className='flex justify-end gap-2'>
        <Button variant='secondary' size='sm' onClick={() => handleOpenChange(false)}>
          Cancel
        </Button>
        <Button size='sm' onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  );

  const renderTrigger = (onClick?: () => void) => {
    if (!label) {
      return (
        <Button
          variant='link-gray'
          className={clsx('relative text-gray-900', className)}
          onClick={onClick}
        >
          {hasActiveFilters ? (
            <div className='absolute top-[-4px] left-[-6px] bg-error-600 rounded-full size-3 z-10' />
          ) : null}
          <FontAwesomeIcon icon={faBarsFilter} size='lg' />
        </Button>
      );
    }

    return (
      <Button
        variant='secondary'
        className={clsx('relative gap-1 shrink-0', className)}
        onClick={onClick}
      >
        {hasActiveFilters ? (
          <div className='absolute top-[-4px] left-[-6px] bg-error-600 rounded-full size-3 z-10' />
        ) : null}
        <FontAwesomeIcon icon={faBarsFilter} className='size-5' />
        {label}
      </Button>
    );
  };

  if (shouldRenderPills) {
    return (
      <div className={clsx('flex flex-wrap items-center gap-2', className)}>{filterGroups}</div>
    );
  }

  if (isMobile) {
    return (
      <>
        {renderTrigger(() => handleOpenChange(true))}
        <Credenza open={open} onOpenChange={handleOpenChange}>
          <CredenzaContent dialogTitle={heading.title}>
            <CredenzaHeader>
              <CredenzaTitle>{heading.title}</CredenzaTitle>
              {heading.description ? (
                <p className='text-gray-500 text-sm'>{heading.description}</p>
              ) : null}
            </CredenzaHeader>
            <CredenzaBody className='flex flex-col gap-y-4 overflow-y-auto'>
              {headerSlot}
              {filterGroups}
            </CredenzaBody>
            {immediate ? null : (
              <CredenzaFooter className={clsx('flex-col gap-4', isWebview && 'pb-6')}>
                <div className='h-px bg-gray-200 w-full' />
                {filterActions}
              </CredenzaFooter>
            )}
          </CredenzaContent>
        </Credenza>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
      <PopoverContent
        className='flex flex-col gap-y-2 bg-white space-y-4 w-[542px] p-6 rounded-xl'
        side='left'
        align='start'
        sideOffset={10}
      >
        <div>
          <p className='text-gray-900 font-semibold text-xl'>{heading.title}</p>
          {heading.description ? (
            <span className='text-gray-500 text-sm'>{heading.description}</span>
          ) : null}
        </div>
        <div className='p-2 flex flex-col gap-y-4'>
          {headerSlot}
          {filterGroups}
          {immediate ? null : (
            <>
              <div className='h-px bg-gray-200' />
              {filterActions}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// TODO: CardFilterUI is too large and should be broken into smaller sub-components
export const Card = CardRootUI;
export const CardHeader = CardHeaderUI;
export const CardTitle = CardTitleUI;
export const CardContent = CardContentUI;
export const CardFilter = CardFilterUI;
export const CardLink = CardLinkUI;
export const CardImg = CardImgUI;
